import type { Camera, Point } from './types';

/** Single zoom step (multiplicative). Zoom-in = `* ZOOM_STEP`, zoom-out =
 * `/ ZOOM_STEP`. Shared by wheel, +/- buttons, and keyboard so one press/click/
 * notch = the same amount (was inconsistent: wheel/keys 10% vs buttons 20%). */
export const ZOOM_STEP = 1.1;

export function uuid(): string {
  return crypto.randomUUID();
}

export function clamp(val: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, val));
}

export function dist(x1: number, y1: number, x2: number, y2: number): number {
  return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
}

export function snapToGrid(val: number, gridSize: number): number {
  return Math.round(val / gridSize) * gridSize;
}

export function screenToWorld(sx: number, sy: number, camera: Camera): Point {
  return {
    x: (sx - camera.offsetX) / camera.zoom,
    y: (sy - camera.offsetY) / camera.zoom,
  };
}

export function worldToScreen(wx: number, wy: number, camera: Camera): Point {
  return {
    x: wx * camera.zoom + camera.offsetX,
    y: wy * camera.zoom + camera.offsetY,
  };
}

export function timeStr(): string {
  const d = new Date();
  return d.getHours().toString().padStart(2, '0') + ':' + d.getMinutes().toString().padStart(2, '0');
}

export function snapToGridCenter(wx: number, wy: number, gridSize: number): Point {
  const cellX = Math.floor(wx / gridSize);
  const cellY = Math.floor(wy / gridSize);
  return {
    x: cellX * gridSize + gridSize / 2,
    y: cellY * gridSize + gridSize / 2,
  };
}

export function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

// ── Viewport culling utilities ──

/** Visible area in world coordinates */
export interface ViewportBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * Compute the visible viewport bounds in world coordinates from camera and screen dimensions.
 * `margin` adds extra pixels on each side to avoid popping at edges.
 */
export function getViewportBounds(
  cam: Camera,
  viewW: number,
  viewH: number,
  margin = 100,
): ViewportBounds {
  return {
    minX: (0 - margin - cam.offsetX) / cam.zoom,
    minY: (0 - margin - cam.offsetY) / cam.zoom,
    maxX: (viewW + margin - cam.offsetX) / cam.zoom,
    maxY: (viewH + margin - cam.offsetY) / cam.zoom,
  };
}

/**
 * AABB test: does a circle at (wx, wy) with given radius overlap the viewport bounds?
 */
export function isInViewport(
  wx: number,
  wy: number,
  radius: number,
  vp: ViewportBounds,
): boolean {
  return (
    wx + radius > vp.minX &&
    wx - radius < vp.maxX &&
    wy + radius > vp.minY &&
    wy - radius < vp.maxY
  );
}

/** Check if a URL points to a WebM video file. */
export function isWebM(url: string): boolean {
  return url.toLowerCase().endsWith(".webm");
}

/** Extract up to 2 initials from a name string. */
export function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}
